import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  TextField,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AttachMoney, ShoppingCart, Search, LocalShipping, StoreMallDirectory, Person, HowToReg } from "@material-ui/icons";
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import api from "../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  container: {
    position: "relative",
    top: 64,
    left: 0,
    right: 0,
    backgroundColor: "#f4f6f8",
    overflowY: "auto",
    padding: theme.spacing(4),
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      top: 56,
      padding: theme.spacing(2),
      paddingTop: theme.spacing(6),
    },
  },
  titulo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: theme.spacing(2, 3),
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2),
    },
  },
  tituloTexto: {
    fontWeight: 700,
    fontSize: "1.4rem",
    color: "#1e293b",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.05rem",
    },
  },
  filtro: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(3),
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1),
      padding: theme.spacing(1),
    },
  },
  dataInput: {
    "& .MuiInputBase-root": {
      borderRadius: 8,
      background: "#f9fafb",
      fontSize: "0.9rem",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: theme.spacing(3),
    boxShadow: "0 1px 5px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
  },
  // smaller card / icon on mobile
  [theme.breakpoints.down("sm")]: {
    card: {
      padding: theme.spacing(2),
      borderRadius: 10,
    },
    iconBox: {
      width: 44,
      height: 44,
      marginBottom: theme.spacing(1.5),
    },
    numero: {
      fontSize: "1.35rem",
    },
    subtitulo: {
      fontSize: "0.85rem",
    },
  },
  iconBox: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  numero: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#111827",
  },
  subtitulo: {
    color: "#6b7280",
    fontWeight: 500,
    fontSize: "0.9rem",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(10),
  },
}));

export default function Dashboard() {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState({ totalPedidos: 0, faturamento: 0, pedidos: [] });
  const [inicio, setInicio] = useState(new Date().toLocaleDateString("sv-SE"));
  const [fim, setFim] = useState(new Date().toLocaleDateString("sv-SE"));

  // Função para contagem por tipo e origem
  function getDashboardContagem(pedidos) {
    let retirada = 0, delivery = 0, manual = 0, selfcheckout = 0, typebot = 0;
    pedidos.forEach(p => {
      const resumo = (p.resumo || "").toLowerCase();
      // Delivery ou retirada
      if (resumo.includes("endereço:")) delivery++;
      else retirada++;

      // Origem (prioridade: selfcheckout > manual > typebot > fallback manual)
      if (resumo.includes("resumo do pedido selfcheckout")) {
        selfcheckout++;
      } else if (resumo.includes("resumo do pedido manual")) {
        manual++;
      } else if (resumo.includes("resumo do pedido apenas")) {
        typebot++;
      } else {
        manual++;
      }
    });
    return { retirada, delivery, manual, selfcheckout, typebot };
  }

  const carregarDashboard = async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard", { params });
      setDados(data);
    } catch (err) {
      console.error("❌ Erro ao carregar dashboard:", err);
      toast.error("Erro ao carregar dados do dashboard");
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltro = () => {
    if (!inicio || !fim) {
      toast.warning("Selecione o período completo");
      return;
    }
    carregarDashboard({ inicio, fim });
  };

  const formatarMoeda = (valor) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }).format(valor || 0);

  useEffect(() => {
    const hoje = new Date().toLocaleDateString("sv-SE"); // ✅ formato yyyy-MM-dd
    setInicio(hoje);
    setFim(hoje);
    carregarDashboard({ inicio: hoje, fim: hoje }); // ✅ já traz o filtro de hoje
  }, []);

  // Debug: mostrar pedidos e resumo
  useEffect(() => {
    if (dados.pedidos && dados.pedidos.length > 0) {
      console.log('Pedidos recebidos:', dados.pedidos);
      dados.pedidos.forEach((p, i) => {
        console.log(`Pedido #${i + 1} resumo:`, p.resumo);
      });
    } else {
      console.log('Nenhum pedido recebido ou array vazio.');
    }
  }, [dados.pedidos]);

  // Calcula as contagens
  const contagem = getDashboardContagem(dados.pedidos || []);

  return (
    <Box className={classes.container}>
      <Paper elevation={0} className={classes.titulo}>
        <Typography className={classes.tituloTexto}>📊 Dashboard</Typography>
      </Paper>

      <Box className={classes.filtro}>
        <TextField
          label="Início"
          type="date"
          value={inicio}
          onChange={(e) => setInicio(e.target.value)}
          className={classes.dataInput}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Fim"
          type="date"
          value={fim}
          onChange={(e) => setFim(e.target.value)}
          className={classes.dataInput}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<Search />}
          onClick={aplicarFiltro}
          style={{
            borderRadius: 8,
            padding: "6px 16px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Buscar
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setInicio(new Date().toLocaleDateString("sv-SE"));
            setFim(new Date().toLocaleDateString("sv-SE"));
            carregarDashboard();
          }}
          style={{
            borderRadius: 8,
            padding: "6px 16px",
            textTransform: "none",
          }}
        >
          Limpar
        </Button>
      </Box>

      {loading ? (
        <Box className={classes.loadingBox}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Grid container spacing={3} alignItems="stretch">

          {/* Card: Pedidos */}
          <Grid item xs={12} sm={6} md={2} style={{ display: 'flex' }}>
            <Paper className={classes.card} style={{ flex: 1 }}>
              <Box className={classes.iconBox} style={{ backgroundColor: "#e0f2fe" }}>
                <ShoppingCart style={{ color: "#0284c7", fontSize: 38 }} />
              </Box>
              <Typography className={classes.numero}>{dados.totalPedidos}</Typography>
              <Typography className={classes.subtitulo}>Total Pedidos</Typography>
            </Paper>
          </Grid>


          {/* Card: Delivery */}
          <Grid item xs={12} sm={6} md={2} style={{ display: 'flex' }}>
            <Paper className={classes.card} style={{ flex: 1 }}>
              <Box className={classes.iconBox} style={{ backgroundColor: "#fef9c3" }}>
                <LocalShipping style={{ color: "#f59e42", fontSize: 38 }} />
              </Box>
              <Typography className={classes.numero}>{contagem.delivery}</Typography>
              <Typography className={classes.subtitulo}>Delivery</Typography>
            </Paper>
          </Grid>


          {/* Card: Retirada */}
          <Grid item xs={12} sm={6} md={2} style={{ display: 'flex' }}>
            <Paper className={classes.card} style={{ flex: 1 }}>
              <Box className={classes.iconBox} style={{ backgroundColor: "#e0e7ff" }}>
                <StoreMallDirectory style={{ color: "#6366f1", fontSize: 38 }} />
              </Box>
              <Typography className={classes.numero}>{contagem.retirada}</Typography>
              <Typography className={classes.subtitulo}>Retirada</Typography>
            </Paper>
          </Grid>


          {/* Card: Typebot */}
          <Grid item xs={12} sm={6} md={2} style={{ display: 'flex' }}>
            <Paper className={classes.card} style={{ flex: 1 }}>
              <Box className={classes.iconBox} style={{ backgroundColor: "#e7fbe8" }}>
                <WhatsAppIcon style={{ color: "#25d366", fontSize: 38 }} />
              </Box>
              <Typography className={classes.numero}>{contagem.typebot}</Typography>
              <Typography className={classes.subtitulo}>Fluxo WhatsApp</Typography>
            </Paper>
          </Grid>


          {/* Card: Manual */}
          <Grid item xs={12} sm={6} md={2} style={{ display: 'flex' }}>
            <Paper className={classes.card} style={{ flex: 1 }}>
              <Box className={classes.iconBox} style={{ backgroundColor: "#f3e8ff" }}>
                <Person style={{ color: "#a21caf", fontSize: 38 }} />
              </Box>
              <Typography className={classes.numero}>{contagem.manual}</Typography>
              <Typography className={classes.subtitulo}>Manual</Typography>
            </Paper>
          </Grid>


          {/* Card: SelfCheckout */}
          <Grid item xs={12} sm={6} md={2} style={{ display: 'flex' }}>
            <Paper className={classes.card} style={{ flex: 1 }}>
              <Box className={classes.iconBox} style={{ backgroundColor: "#dcfce7" }}>
                <HowToReg style={{ color: "#16a34a", fontSize: 38 }} />
              </Box>
              <Typography className={classes.numero}>{contagem.selfcheckout}</Typography>
              <Typography className={classes.subtitulo}>SelfCheckout</Typography>
            </Paper>
          </Grid>

          {/* Card: Faturamento */}
          <Grid item xs={12} sm={6} md={2} style={{ display: 'flex' }}>
            <Paper className={classes.card} style={{ flex: 1 }}>
              <Box className={classes.iconBox} style={{ backgroundColor: "#fff7ed" }}>
                <AttachMoney style={{ color: "#ea580c", fontSize: 38 }} />
              </Box>
              <Typography className={classes.numero}>{formatarMoeda(dados.faturamento)}</Typography>
              <Typography className={classes.subtitulo}>Faturamento</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
